import React, { useMemo, useState } from 'react';
import { Calculator, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * Simple mortgage calculator for the property detail page.
 *
 * Conversion logic:
 *   Showing visitors the *monthly* cost is one of the highest-impact CTAs in
 *   real-estate UX research (Funda, Idealista, RightMove all do this). It
 *   transforms an abstract €489 000 sticker shock into "around €1 950/month
 *   for me" — much easier to act on.
 *
 * Math:
 *   Annuity formula: PMT = P * r / (1 - (1+r)^-n)
 *   where P = loan amount, r = monthly rate, n = number of months.
 *   Default down payment 20% (NL norm), 30y term, current EU rate ~3.5%.
 */
const calcMonthly = (price, down, ratePct, years) => {
  const principal = Math.max(0, price - down);
  if (principal <= 0) return 0;
  const months = years * 12;
  const r = ratePct / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - (1 + r) ** -months);
};

const fmt = (n) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(Math.round(n));

const MortgageCalculator = ({ price }) => {
  const { language } = useLanguage();
  const isNL = language === 'nl';
  const [open, setOpen] = useState(false);
  const [downPercent, setDownPercent] = useState(20);
  const [ratePct, setRatePct] = useState(3.5);
  const [years, setYears] = useState(30);

  const downPayment = Math.round(price * downPercent / 100);
  const monthly = useMemo(() => calcMonthly(price, downPayment, ratePct, years),
    [price, downPayment, ratePct, years]);

  return (
    <div style={styles.wrap} data-testid="mortgage-calculator">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={styles.header}
        aria-expanded={open}
        data-testid="mortgage-toggle"
      >
        <Calculator size={18} color="#C5A059" />
        <span style={styles.headerLabel}>
          {isNL ? 'Hypotheekberekening' : 'Mortgage estimate'}
        </span>
        <span style={styles.headerValue}>
          {fmt(monthly)} <small style={styles.suffix}>{isNL ? '/maand' : '/month'}</small>
        </span>
        <ChevronDown size={18} color="#8C8B88" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>

      {open && (
        <div style={styles.body}>
          <Field
            label={isNL ? 'Aanbetaling' : 'Down payment'}
            value={`${downPercent}% (${fmt(downPayment)})`}
            input={
              <input
                type="range" min="0" max="50" step="5"
                value={downPercent}
                onChange={(e) => setDownPercent(Number(e.target.value))}
                style={styles.range}
                data-testid="mortgage-down"
              />
            }
          />
          <Field
            label={isNL ? 'Rente' : 'Interest rate'}
            value={`${ratePct.toFixed(1)} %`}
            input={
              <input
                type="range" min="1" max="7" step="0.1"
                value={ratePct}
                onChange={(e) => setRatePct(Number(e.target.value))}
                style={styles.range}
                data-testid="mortgage-rate"
              />
            }
          />
          <Field
            label={isNL ? 'Looptijd' : 'Term'}
            value={`${years} ${isNL ? 'jaar' : 'years'}`}
            input={
              <input
                type="range" min="10" max="30" step="5"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                style={styles.range}
                data-testid="mortgage-years"
              />
            }
          />
          <p style={styles.disclaimer}>
            {isNL
              ? 'Indicatieve berekening. Werkelijke voorwaarden hangen af van uw bank.'
              : 'Indicative calculation. Actual terms depend on your bank.'}
          </p>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, input }) => (
  <div style={styles.field}>
    <div style={styles.fieldHead}>
      <span style={styles.fieldLabel}>{label}</span>
      <span style={styles.fieldValue}>{value}</span>
    </div>
    {input}
  </div>
);

const styles = {
  wrap: { backgroundColor: 'white', borderWidth: '1px', borderStyle: 'solid', borderColor: '#EAE5DD', marginBottom: '2rem' },
  header: {
    display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%',
    padding: '1rem 1.25rem', background: 'transparent', border: 'none',
    cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
  },
  headerLabel: { color: '#1A1918', fontWeight: 600, fontSize: '0.92rem' },
  headerValue: { marginLeft: 'auto', color: '#C5A059', fontWeight: 700, fontSize: '1.1rem', fontFamily: "'Playfair Display', serif" },
  suffix: { color: '#8C8B88', fontWeight: 400, fontSize: '0.8rem' },
  body: { padding: '0 1.25rem 1.25rem', borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: '#EAE5DD' },
  field: { padding: '1rem 0' },
  fieldHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' },
  fieldLabel: { color: '#585755', fontSize: '0.85rem' },
  fieldValue: { color: '#1A1918', fontWeight: 600, fontSize: '0.9rem' },
  range: { width: '100%', accentColor: '#C5A059' },
  disclaimer: { color: '#8C8B88', fontSize: '0.75rem', margin: '0.5rem 0 0', fontStyle: 'italic' },
};

export default MortgageCalculator;
